import { SlicePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChange, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { MapMarker } from '@angular/google-maps';
import { BookingModel, BookingService, MapService, NavigationService } from 'app-components';
import { AppIntegrationService, AppSettingsService, Coordinates } from 't4-app-integration';
import { ComponentBase, LocationInfo, TranslationService } from 't4core';
import { IdleDialog } from '../../../app/MyPage/Dialogs/idle-dialog/idle-dialog';
import { MarkerClusterer, Cluster, ClusterStats } from "@googlemaps/markerclusterer";

export enum MapMode {
  Local = 'Local',
  Pickup = 'Pickup',
  Return = 'Return'
}
 
@Component({
  selector: 'location-map',
  templateUrl: 'location-map.html',
  standalone: false
})
export class LocationMapComponent extends ComponentBase implements OnInit, OnChanges, OnDestroy {
  @Input() public locations: LocationInfo[] = [];
  @Input() public selectedLocation: LocationInfo;
  @Input() public selectedPickupLocation: LocationInfo;
  @Input() public useCluster: boolean = true;

  @Input() public mode: MapMode = MapMode.Local;
  @Input() public showPlaceSearch: boolean = false;

  @Output() public selectedLocationChange: EventEmitter<LocationInfo> = new EventEmitter<LocationInfo>();
  @Output() public locationConfirmed: EventEmitter<LocationInfo> = new EventEmitter<LocationInfo>();


  public showSearch: boolean = false;
  public hideExternalLocations: boolean = true;

  public selectedDistance: number = 0;
  public selectedDuration: number = 0;
  public selectedDurationHours: number = 0;
  public selectedDurationMinutes: number = 0;
  public wasSelectedMarkerHidden: boolean = false;
  public isLocationPreSelected = false;

  public bookingState: BookingModel;


  public clusterOrb: string = "#4f5bb2";
  public clusterText: string = "rgba(255,255,255,0.9)";
  private themeVarsLoaded = false;

  //Google maps
  @ViewChild('gmap', { static: true }) gmapElement: ElementRef;

  private directions: google.maps.DirectionsService;
  private directionsRenderer: google.maps.DirectionsRenderer;

  private map: google.maps.Map;
  private markers = [];
  private clusterer: MarkerClusterer;
  private searchPin: google.maps.Marker;
  private infoWindow: google.maps.InfoWindow;
  private zoomDistances = [
    { zoom: 17, maxDistance: 0.5 },
    { zoom: 16, maxDistance: 1 },
    { zoom: 14, maxDistance: 2 },
    { zoom: 12, maxDistance: 4 },
    { zoom: 10, maxDistance: 8 },
    { zoom: 9, maxDistance: 16 },
    { zoom: 8, maxDistance: 32 },
    { zoom: 7, maxDistance: 64 },
    { zoom: 6, maxDistance: 128 },
    { zoom: 5, maxDistance: 256 },
    { zoom: 4, maxDistance: 512 },
    { zoom: 3, maxDistance: 1024 },
    { zoom: 2, maxDistance: 2048 },
    { zoom: 1, maxDistance: 4096 }
  ];

  private searchIsDirty: boolean = false;

  private groupPins: any = {};


  constructor(
    private navigator: NavigationService,
    el: ElementRef,
    private zone: NgZone,
    private appSettings: AppSettingsService,
    private aviationService: NavigationService,
    private translationsService: TranslationService,
    private appService: AppIntegrationService,
    private bookingService: BookingService,
    private mapService: MapService) {
    super();

    this.bookingState = this.bookingService.getBookingState();
  }

  public async ngOnDestroy() {
    this.markers.map(x => {
      if (x.marker) x.marker.setMap(null);
      if (x.circle) x.circle.setMap(null);
    });
    if (this.searchPin) this.searchPin.setMap(null);
    if (this.directionsRenderer) this.directionsRenderer.setMap(null);

    window['bookFromMap'] = undefined;

    // Detach the map div to a hidden container to preserve it for reuse
    // This prevents Angular from destroying the map div when this component is destroyed
    this.mapService.detachMapInstance('location-map');
    this.map = null;

    super.ngOnDestroy();
  }

  public async ngOnInit() {

    const { } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

    this.bookingState = await this.bookingService.getBookingState();

    // Add a js function to enable events from button in map tooltip
    window['bookFromMap'] = () => {
      this.zone.run(() => {
        this.locationConfirmed.emit(this.selectedLocation); 
      });
    };

    var latlng = new google.maps.LatLng(
      this.Settings.clientSettings.DefaultLatitude,
      this.Settings.clientSettings.DefaultLongitude
    ); // This sets the default location, before user location is found

   

    var mapProp = {
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      key: "AIzaSyDnRP0crv0lY8cWvoxVn4ml2AqD3_JKG20",
      disableDefaultUI: true,
      mapId: '17c55b6aa3c322c5',
      zoom: 10,
      center: latlng,
      gestureHandling: 'auto'
    };

    if (this.appSettings.appType == 'app') {
      mapProp.gestureHandling ="greedy";
    }


    if (this.gmapElement && this.gmapElement.nativeElement) {
      this.map = this.mapService.getMapInstance('location-map', this.gmapElement.nativeElement, mapProp);

      this.map.setCenter({ lat: this.Settings.clientSettings.DefaultLatitude, lng: this.Settings.clientSettings.DefaultLongitude });
      this.map.setZoom(5);

      this.map.addListener("click", (event: any) => {

        if (this.infoWindow)
          this.infoWindow.close();

        // If the event has a placeId, use it.
        if (event.placeId) {
          event.stop();
        }
      });

      const searchedCoords = this.bookingService.getSearchedCoordinates();


      if (this.locations) {
        this.updateMarkers();


        //If we Param pickupLocationid is the Same as its the selectedLocation, we pre select that, even if GPS is not on.
        if (this.appSettings.getParam("pickupLocationId") != null && this.selectedLocation?.Id.toString() == this.appSettings.getParam("pickupLocationId")) {
          var preselectedLocation = this.locations.find(x => x.Id.toString() == this.appSettings.getParam("pickupLocationId"))

          const preLocationCords: Coordinates = {
            Latitude: preselectedLocation.Latitude,
            Longitude: preselectedLocation.Longitude
          };

          this.gotoPlace(preLocationCords);
        }
        else if (searchedCoords) {
          this.gotoPlace(searchedCoords, null, true);
        }

        var coords = await this.appService.getCoordinates();
        if (coords) {
          this.isLocationPreSelected = true;
          this.gotoPlace(coords);
        }
      }

    }
  }

  public firstChangeDone: boolean = false;

  async ngOnChanges(changes: SimpleChanges) {
    if (!this.map) return;
    this.updateMarkers();

    if ((changes["selectedLocation"] && !changes["selectedLocation"].previousValue && changes["selectedLocation"].currentValue)) {
      this.goneToPlace = false;
      this.isLocationPreSelected = false;
      if (this.selectedLocation) this.gotoPlace({ Latitude: this.selectedLocation.Latitude, Longitude: this.selectedLocation.Longitude }, null, true);
    }

    //Catches the first change, and if a pin already exist.
    //Resolves the cold loading into the map, and the pin issues when the pin temporarly sets to closet station.
    if (!this.firstChangeDone && this.searchPin) {
      this.firstChangeDone = true;
      return;
    }

    if ((changes["locations"] && !changes["locations"].previousValue && changes["locations"].currentValue)) {
      if (this.selectedLocation) {
        this.isLocationPreSelected = false;
        this.gotoPlace({ Latitude: this.selectedLocation.Latitude, Longitude: this.selectedLocation.Longitude }, null, true);
      } else {
        var coords = await this.appService.getCoordinates();
        if (coords) this.gotoPlace(coords);
      }
    }
  }

  private _tickCounter: number = 0;
  public async secondTick() {
    if (this._isDestroyed) return;

    this._tickCounter++;
    if (!this.searchIsDirty && this._tickCounter == 15) {
      var coords = await this.appService.getCoordinates(false);
      if (!coords || this._isDestroyed) return;

      if (this.searchPin) this.searchPin.setPosition({ lat: coords.Latitude, lng: coords.Longitude });
      this._tickCounter = 0;
    }
  }

  public toggleSearch() {
    this.showSearch = !this.showSearch;
  }

  private goneToPlace: boolean = false;

  public async search(coords: Coordinates) {
    this.selectedLocation = null;
    this.searchIsDirty = true;
    this.goneToPlace = false;
    this.isLocationPreSelected = false;
    await this.gotoPlace(coords);
  }

  public setPin(coords: Coordinates) {
    // Remove existing pin
    if (this.searchPin) this.searchPin.setMap(null);

    // Add pin
    this.searchPin = new google.maps.Marker({
      map: this.map,
      position: { lat: coords.Latitude, lng: coords.Longitude },
      animation: google.maps.Animation.DROP,
      zIndex: 2001,
      clickable: false
    });
    this.searchPin.setZIndex(175);
  }

  public async gotoPlace(coords: Coordinates, adaptiveZoom: boolean = false, ignorePin: boolean = false) {

    if (this._isDestroyed || !this.map) return;

    // Check if coords are valid
    if (!coords || coords.Latitude == null || coords.Longitude == null) return;

    // Ignore if coords match default location
    if (
      coords.Longitude === this.Settings.clientSettings.DefaultLongitude &&
      coords.Latitude === this.Settings.clientSettings.DefaultLatitude
    ) return;

    if (ignorePin == false) {
      this.setPin(coords);
    }

    if (this.mode === MapMode.Return) {

      if (this.selectedLocation) {

        this.setPin({
          Latitude: this.selectedLocation.Latitude,
          Longitude: this.selectedLocation.Longitude
        });


        let marker = this.markers.find(x => x.locationId == this.selectedLocation.Id);
        this.openPopup(marker.marker, this.selectedLocation);

        setTimeout(() => {
          this.map.panTo({
            lat: this.selectedLocation.Latitude,
            lng: this.selectedLocation.Longitude
          });
          this.map.setZoom(5);
        }, 50);
      }


    } else if (this.locations && !this.goneToPlace && coords && coords.Latitude && coords.Longitude) {
      this.goneToPlace = true;

      // Update distances
      this.locations.map(location => location.Distance = this.appService.getDistance(coords, { Latitude: location.Latitude, Longitude: location.Longitude }));

      // Zoom into the closest location
      var closest;
      if (this.bookingState.objectCategory)
        closest = this.locations.filter(x => (this.mode == MapMode.Local || x.OpenForPickup) && x.HasFamilies?.includes(this.bookingState.objectCategory.Id) && (!this.hideExternalLocations || !x.ExternalBookingUri))
          .sort((a: LocationInfo, b: LocationInfo) => a.Distance <= b.Distance ? -1 : 1)[0];
      else {
        closest = this.locations.filter(x => (this.mode == MapMode.Local || x.OpenForPickup) && (!this.hideExternalLocations || !x.ExternalBookingUri))
          .sort((a: LocationInfo, b: LocationInfo) => a.Distance <= b.Distance ? -1 : 1)[0];
      }

      // #846: If we have a selected Category, we only check the station that have the type.
      // Only if we dont already have the country.
      if (closest) {
        //Saves the closet location, if there is none, we have Sweden as fallback.
        await this.appSettings.setAnticipatedUserCountry(closest.Country);
      }

      // Already a selected location? Use it!
      if (this.selectedLocation)
        closest = this.selectedLocation;

      var list = [];
      list.push(new google.maps.LatLng(closest.Latitude, closest.Longitude));
      if (this.searchPin) list.push(this.searchPin.getPosition());
      if (this.selectedPickupLocation) list.push(new google.maps.LatLng(this.selectedPickupLocation.Latitude, this.selectedPickupLocation.Longitude));

      setTimeout(() => {

        if (this.isLocationPreSelected) {
          this.map.fitBounds(this.CalculateBounds(list), 15);
        } else if (this.searchIsDirty) {
          console.log("no selected location")
          this.map.panTo({ lat: coords.Latitude, lng: coords.Longitude });
        }
  
        //this.map.panTo({ lat: coords.Latitude, lng: coords.Longitude });
        //this.map.setZoom(14);


        let marker = this.markers.find(x => x.locationId == closest.Id);
        if (marker) {
          if (this.appSettings.contextId == "KanLeiesUnmanned_API" && !closest.OpenForPickup) {
            this.openFutureLocatioInfo(marker.marker, closest);
          } else {
            this.openPopup(marker.marker, closest);
          }
        }
      }, 50);
    } else if (coords && coords.Latitude && coords.Longitude) {
      setTimeout(() => {
        this.map.panTo({ lat: coords.Latitude, lng: coords.Longitude });
        this.map.setZoom(8);
      }, 50);
    }

  }

  // Remove and re-add all markers on the map
  public async updateMarkers() {
    if (this.locations) {
      for (var item of this.locations) {
        this.addLocation(item, 50);
      }
    }

    // Add a marker clusterer to manage the markers.
    var map = this.map;
    var markers = [];
    this.markers.map(x => markers.push(x.marker));
  
    let renderer = {
      render: ({ count, position }, stats) => {
        // attempt to read CSS variables from loaded theme

        if (!this.themeVarsLoaded)
          this.loadThemeVarsFromCss();

        // create svg url with fill color
        const svg = window.btoa(`
          <svg fill="${this.clusterOrb}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
            <circle cx="120" cy="120" opacity=".6" r="70" />
            <circle cx="120" cy="120" opacity=".3" r="90" />
            <circle cx="120" cy="120" opacity=".2" r="110" />
            <circle cx="120" cy="120" opacity=".1" r="130" />
          </svg>`);

        // create marker using svg icon
        return new google.maps.Marker({
          position,
          icon: {
            url: `data:image/svg+xml;base64,${svg}`,
            scaledSize: new google.maps.Size(45, 45),
          },
          label: {
            text: String(count),
            color: this.clusterText,
            fontSize: "12px",
          },
          // adjust zIndex to be above other markers
          zIndex: 1000 + count,
        });
      }
    }

    if (this.useCluster) {

      if (!this.clusterer) this.clusterer = new MarkerClusterer({ map, markers, renderer });

      this.clusterer.addListener("clusteringend", (clusterer) => {
        if (!this.selectedLocation) {
          this.wasSelectedMarkerHidden = false;
          return;
        }
        const selectedMarker = this.markers.find(
          m => m.locationId === this.selectedLocation.Id
        )?.marker;
        if (!selectedMarker) return;
        const isHiddenNow = selectedMarker.getMap() === null;
        if (!this.wasSelectedMarkerHidden && isHiddenNow) {
          this.selectedLocation = null;
          this.selectedLocationChange.emit(null);
          if (this.infoWindow) {
            this.infoWindow.close();
            //this.infoWindow.setPosition(null);
            this.infoWindow.set("anchor", null);
          }
        }

        this.wasSelectedMarkerHidden = isHiddenNow;
      });
    }

  }

  // Create a pin and add it to the map
  private addLocation(item: LocationInfo, delay: number) {
    var marker = this.markers.find(x => x.locationId == item.Id)?.marker;
    var firstRun = false;
    if (!marker) {
      marker = new google.maps.Marker({
        position: new google.maps.LatLng(item.Latitude, item.Longitude),
        icon: "",
        title: item.Name + ", " + item.Address,
        zIndex: 2000,
      });


      var circlee = new google.maps.Circle({
        strokeColor: "#ADD8E6",
        strokeOpacity: 0.8,
        fillColor: "#32CD32",
        strokeWeight: 1,
        map: this.map,
        fillOpacity: 0.35,
        radius: item.TrailerPointRadius
      })


      circlee.bindTo('center', marker, 'position');

      this.markers.push({ locationId: item.Id, location: item, marker: marker, circle: circlee });
      firstRun = true;
    }

    // Return && Selected pickup?
    if (this.mode == MapMode.Return && item.Id == this.selectedPickupLocation.Id) {
      this.setMarkerImage(marker, "Pin_Pickup", false, item);
      marker.setZIndex(200);
    }
    // Return && Not open for return?
    else if (this.mode == MapMode.Return && !item.OpenForReturn) {
      this.setMarkerImage(marker, "Pin_Disabled", false, item);
      marker.setZIndex(100);

      if (firstRun) {
        marker.addListener('click', () => {
          this.openDisabledReturnInfo(marker, item);
        });
      }
    }
    // Pickup && Not open for pickup?
    else if (this.mode == MapMode.Pickup && !item.OpenForPickup) {
      this.setMarkerImage(marker, "Pin_Disabled", false, item);
      marker.setZIndex(100);

      if (firstRun) {
        marker.addListener('click', () => {
          this.openDisabledPickupInfo(marker, item);
        });
      }
    }
    // Return && Selected?
    else if (this.mode == MapMode.Return && item == this.selectedLocation) {
      this.setMarkerImage(marker, "Pin_Return", false, item);
      marker.setZIndex(300);
      // Standard marker
    } else {

      if (this.bookingState.objectCategory != null && !item.HasFamilies?.includes(this.bookingState.objectCategory.Id)) {
        //Adds a opacity to the marker, and in turn adds a grayscale through css.
        this.setMarkerImage(marker, "Pin", true, item);
        marker.setZIndex(100);
      }
      else {
        this.setMarkerImage(marker, "Pin", false, item);
        marker.setZIndex(150);
      }

      if (firstRun) {
        marker.addListener('click', () => {
          var list = [];
          list.push(new google.maps.LatLng(item.Latitude, item.Longitude));
          if (this.searchPin) list.push(this.searchPin.getPosition());
          if (this.selectedPickupLocation) list.push(new google.maps.LatLng(this.selectedPickupLocation.Latitude, this.selectedPickupLocation.Longitude));
          this.map.panTo({ lat: item.Latitude, lng: item.Longitude });
          //this.map.panToBounds(this.CalculateBounds(list), 25);

          setTimeout(async () => {
            if (item.ExternalBookingUri) {
              this.openExternal(marker, item);
            }
            else if (this.appSettings.contextId == "KanLeiesUnmanned_API" && !item.OpenForPickup) {
              this.openFutureLocatioInfo(marker, item);
            }
            else if (this.bookingState.objectCategory != null && !item.HasFamilies?.includes(this.bookingState.objectCategory.Id)) {
              this.openNoObjectWithFilter(marker, item);
            }
            else {
              await this.openPopup(marker, item);
            }
            this.updateMarkers();
          }, 50);
        });
      }
    }

    if (this.hideExternalLocations && item.ExternalBookingUri) {
      this.setMarkerImage(marker, "Pin", true, item);
      marker.setZIndex(100);
    }

    if(!this.useCluster) marker.setMap(this.map);
    return marker;
  }

  private CalculateBounds(list: google.maps.LatLng[]): google.maps.LatLngBounds {
    var x0, x1, y0, y1;
    for (var latLng of list) {
      if (x0 == null) {
        x0 = x1 = latLng.lat();
        y0 = y1 = latLng.lng();
      } else {
        if (latLng.lat() > x1) x1 = latLng.lat();
        if (latLng.lat() < x0) x0 = latLng.lat();
        if (latLng.lng() > y1) y1 = latLng.lng();
        if (latLng.lng() < y0) y0 = latLng.lng();
      }
    }

    if (list.length > 1) {
      y0 -= Math.max((y1 - y0) * 0.5, 0.01);
      y1 += Math.max((y1 - y0) * 0.2, 0.01);
    } else {
      x0 -= 0.1;
      x1 += 0.1;
      y0 -= 0.1;
      y1 += 0.1;
    }


    return new google.maps.LatLngBounds(new google.maps.LatLng(x0, y0), new google.maps.LatLng(x1, y1));
  }

  private valBook = "";
  private async openPopup(marker: google.maps.Marker, location: LocationInfo) {
    this.goneToPlace = true;

    if (this.valBook == "") {
      if (this.mode == MapMode.Local)
        this.valBook = await this.translationsService.translate("Intellitrailer.Home.cmdBook2", "Book a ticket");
      else
        this.valBook = await this.translationsService.translate("Intellitrailer.ReturnLocation.cmdSelect", "Select");
    }

    if (!this.infoWindow) this.infoWindow = new google.maps.InfoWindow();
    this.infoWindow.setContent(`<div class="map-box-title">${location.Name}</div><div class="map-box-text">${location.Address}<br/>${location.City}<br/><button type="button" class="map-button" onClick="bookFromMap()">${this.valBook}</button></div>`);
    this.infoWindow.open(this.map, marker);
    this.selectedLocation = location;
    this.selectedLocationChange.emit(this.selectedLocation);

    // Find routes and calculate distances if in return mode
    if (this.mode == MapMode.Return) {
      if (!this.directions) this.directions = new google.maps.DirectionsService();
      this.directions.route({
        origin: new google.maps.LatLng(this.selectedPickupLocation.Latitude, this.selectedPickupLocation.Longitude),
        destination: new google.maps.LatLng(this.selectedLocation.Latitude, this.selectedLocation.Longitude),
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false
      }, (routes, status) => {
        if (status == google.maps.DirectionsStatus.OK) {
          this.selectedDistance = 0;
          this.selectedDuration = 0;
          routes.routes[0].legs.map(x => this.selectedDistance += (x.distance.value / 1000.0));
          routes.routes[0].legs.map(x => this.selectedDuration += (x.duration.value / 60.0 / 60.0));
          this.selectedDurationHours = Math.floor(this.selectedDuration);
          this.selectedDurationMinutes = (this.selectedDuration - this.selectedDurationHours) * 60.0;

          if (!this.directionsRenderer) this.directionsRenderer = new google.maps.DirectionsRenderer({ suppressMarkers: true, preserveViewport: true });
          this.directionsRenderer.setDirections(routes);
          this.directionsRenderer.setMap(this.map);
        }
      });
    }
  }

  private async openFutureLocatioInfo(marker: google.maps.Marker, location: LocationInfo) {
    var message = await this.Translate.translate('Intellitrailer.MapComponent.msgLocationIsNotActive', 'This locations isn\'t yet active in Use4Free.');

    var cnt = `<div class="map-box-title disabled">${location.Name}</div><div class="map-box-text">${message}</div>`;
    if (this.appSettings.appType != 'app' && this.selectedLocation.RentalLocationGroupId != null && this.selectedLocation.RentalLocationGroupId == 4943) // Hornbach GroupId
      cnt += `<br/><a href="https://www.hornbach.se/service/hyr-lastbil-och-lana-slap/ " target="_blank">Klicka här för information och bokning</button>`

    if (!this.infoWindow) this.infoWindow = new google.maps.InfoWindow();
    this.infoWindow.setContent(cnt);
    this.infoWindow.open(this.map, marker);
  }

  private async openDisabledPickupInfo(marker: google.maps.Marker, location: LocationInfo) {
  
    var message = await this.Translate.translate('Intellitrailer.MapComponent.msgLocationTemporarlyClosedPickup', 'Station is currently closed for pickup.')

    if (!this.infoWindow) this.infoWindow = new google.maps.InfoWindow();
    this.infoWindow.setContent(`<div class="map-box-title disabled">${location.Name}</div><div class="map-box-text">${message}</div></div>`);
    this.infoWindow.open(this.map, marker);
  }

  private async openDisabledReturnInfo(marker: google.maps.Marker, location: LocationInfo) {
    var message = await this.Translate.translate('Intellitrailer.MapComponent.msgLocationTemporarlyClosedReturn', 'Station is currently closed for returns.')

    if (!this.infoWindow) this.infoWindow = new google.maps.InfoWindow();
    this.infoWindow.setContent(`<div class="map-box-title disabled" >${location.Name}</div><div class="map-box-text">${message}.</div></div>`);
    this.infoWindow.open(this.map, marker);
  }

  private async openNoObjectWithFilter(marker: google.maps.Marker, location: LocationInfo) {
    var message = await this.Translate.translate('Intellitrailer.MapComponent.msgNoObjectOfSelectedKind', 'This location has no rental object of the selected type.');

    if (!this.infoWindow) this.infoWindow = new google.maps.InfoWindow();
    this.infoWindow.setContent(`<div class="map-box-title disabled">${location.Name}</div><div class="map-box-text">${message}</div></div>`);
    this.infoWindow.open(this.map, marker);
  }

  private async openExternal(marker: google.maps.Marker, location: LocationInfo) {
    var message = await this.Translate.translate('Intellitrailer.MapComponent.msgExternalLocation', 'This locations isn\'t yet available in Brenderup Rental.');
    var link = await this.Translate.translate('Intellitrailer.MapComponent.lnkExternalBooking', 'Click here to book');

    var cnt = `<div class="map-box-title">${location.Name}</div><div class="map-box-text">${message}</div>`;
    cnt += `<br/><a href="${location.ExternalBookingUri}" target="_blank" style="color: red;">${link}</button>`

    if (!this.infoWindow) this.infoWindow = new google.maps.InfoWindow();
    this.infoWindow.setContent(cnt);
    this.infoWindow.open(this.map, marker);
  }

  private async setMarkerImage(marker: any, type: string, disabled: boolean = false, item: LocationInfo) {
    marker.setOpacity(disabled ? 0.33 : 1.0);

    const theme = this.appSettings.settings.theme || 'Default';

    if (this.Settings.clientSettings.UsePinFromLocalGroup) {

      if (this.groupPins["Group_" + item.RentalLocationGroupId]) {
        marker.setIcon(this.groupPins["Group_" + item.RentalLocationGroupId]);
        return;
      }
      var pin = await this.Api.get<string>("Data/GetImageUri", { type: 1, entityId: item.RentalLocationGroupId });
      if (!pin.includes("ObjectType_Default")) {
        marker.setIcon(pin);
        this.groupPins["Group_" + item.RentalLocationGroupId] = pin;
        return;
      }
    }

    marker.setIcon("/Styles/" + theme + "/" + type + ".png?");
  }

  public toggleHideExternalLocations(value) {
    this.hideExternalLocations = value.checked;
    this.updateMarkers();
  }

  private async loadThemeVarsFromCss(): Promise<void> {
    if (this.themeVarsLoaded) return;

    this.themeVarsLoaded = true;
    const theme = this.appSettings?.settings?.theme || 'Default';
    const href = `/Styles/${theme}/Style.css`;

    const response = await fetch(href);
    if (!response.ok) return;

    const cssText = await response.text();

    this.clusterOrb =
      this.extractCssVar(cssText, '--cluster-orb') || '#4f5bb2';

    this.clusterText =
      this.extractCssVar(cssText, '--cluster-text') || 'rgba(255,255,255,0.9)';

  }


  private extractCssVar(css: string, varName: string): string | null {
    const regex = new RegExp(`${varName}\\s*:\\s*([^;]+);`);
    const match = css.match(regex);
    return match ? match[1].trim() : null;
  }


}

